import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  DialogTitle,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { COMPONENT_NAMES, SCREEN_NAMES, trackAnalyticsEvent } from '../../../constants/analyticsMeta';
import { useCustomerInfo, useIsTestDriveUser, useQalert } from '../../../utility/services/customHooks';
import ThreeBarLoader from '../../shared/ThreeBarLoader';
import ApiService from '../../../api/restApi';

import Router from 'next/router';
import { v4 as uuid } from 'uuid';
import { delayTimer } from '../../../utility/cr-common';
import { DEFAULT_INTERNAL_LINKING_FILTERS_OBJ } from '../../../constants/formMeta/growthTrends';
import { validateIlModuleName } from '../../../utility/common';
import { API_STATUS, TEMP_IL_UNIQUE_NAME } from '../../../constants/common';

const useLoginCustomerInfo = () => {
  const isTestDriveUser = useIsTestDriveUser();
  const customerInfoFromState = useSelector((state) => state.root.customerInfo);
  const customerInfo = useCustomerInfo(customerInfoFromState);

  if (!isTestDriveUser) {
    return customerInfo;
  }

  return undefined;
};

const AddNewLink = ({ isNewInternalLinking = false }) => {
  const [openAddNewPage, setOpenAddNewPage] = useState(false);
  const [loader, setLoader] = useState(false);

  const loginCustomerInfo = useLoginCustomerInfo();

  const [, setSnackBar] = useQalert();

  const hideLoader = () => {
    setTimeout(() => {
      setLoader(false);
    }, 500);
  };

  const [linkName, setLinkName] = useState('');
  const [addNewLinkLoading, setAddNewLinkLoading] = useState(false);

  const [isOpenAutoNomousILForm, setIsOpenAutoNomousILForm] = useState(false);
  const [moduleNameError, setModuleNameError] = useState('');

  useEffect(() => {
    setIsOpenAutoNomousILForm(false);
  }, []);

  const currentIlListDetails = useSelector((state) => state?.root?.autoNomousInternalLinkingDetails) as {
    status: string;
    list: any[];
  };

  const setTempLinkingNameHandler = useCallback(
    (reset = false) => {
      if (currentIlListDetails?.status === API_STATUS.SUCCESS && currentIlListDetails?.list) {
        const currentUsedNamesByUser: any[] = [];
        for (const ilData of currentIlListDetails?.list || []) {
          currentUsedNamesByUser?.push(
            ilData?.['Overall Metrics']?.currentUsedName?.publishedName?.toLowerCase(),
            ilData?.['Overall Metrics']?.currentUsedName?.draftName?.toLowerCase()
          );
        }

        const randomNumber = Math.floor(Math.random() * 196);
        setLinkName(reset ? '' : TEMP_IL_UNIQUE_NAME?.[randomNumber] || TEMP_IL_UNIQUE_NAME[0]);
      }
    },
    [currentIlListDetails?.list, currentIlListDetails?.status]
  );

  const onCloseHandler = useCallback(() => {
    setModuleNameError('');
    setIsOpenAutoNomousILForm(false);
    setTempLinkingNameHandler(true);
  }, [setTempLinkingNameHandler]);

  const trackAnalyticsEventCustom = useCallback(
    ({ eventName, eventFor = '', label = '', extraPayload = {} }) => {
      trackAnalyticsEvent({
        eventFor: eventFor,
        buttonText: ``,
        eventName: eventName,
        payload: {
          label: label,
          event_name: eventName,
          component_name: isNewInternalLinking
            ? COMPONENT_NAMES.autonomous_internal_linking
            : COMPONENT_NAMES.internal_linking_recommendations,
          screen_name: isNewInternalLinking
            ? SCREEN_NAMES.autonomous_internal_linking
            : SCREEN_NAMES.internal_linking_recommendations,
          ...(extraPayload || {}),
        },
      });
    },
    [isNewInternalLinking]
  );

  const addNewPageBoxHandler = useCallback(() => {
    const eventTrackDetails = {
      eventName: isNewInternalLinking ? 'Click - Add Linking Module' : 'Add Linking Module',
      eventFor: 'button',
      label: isNewInternalLinking ? 'Click - Add Linking Module' : 'Add Linking Module',
    };
    trackAnalyticsEventCustom(eventTrackDetails);
    if (isNewInternalLinking) {
      setTempLinkingNameHandler();
      setIsOpenAutoNomousILForm(true);
    } else {
      setOpenAddNewPage(!openAddNewPage);
      setLoader(true);
    }
  }, [isNewInternalLinking, openAddNewPage, setTempLinkingNameHandler, trackAnalyticsEventCustom]);

  const addNewLinkHandler = useCallback(async () => {
    const moduleId = uuid();
    const eventTrackDetails = {
      eventName: 'Linking Module Added Successfully',
      extraPayload: {
        module_name: linkName,
        module_id: moduleId,
      },
    };
    try {
      trackAnalyticsEventCustom({ ...eventTrackDetails, eventName: 'Click - Add New Link' });
      const invalidNameErrorMessage = validateIlModuleName(linkName);
      setModuleNameError(invalidNameErrorMessage);
      if (invalidNameErrorMessage) {
        return false;
      }
      setAddNewLinkLoading(true);
      const payload = {
        module_id: moduleId,
        module_name: linkName,
        module_details: {
          ...(DEFAULT_INTERNAL_LINKING_FILTERS_OBJ || {}),
        },
        isAlreadySampleOutputExist: false,
        validateName: true,
        subCustomerPath: '',
        isNewInternalLinking: true,
      };
      payload.module_details['Overall Metrics'].Module_Name = linkName;
      const result = await ApiService.post(
        `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/discoverability/internal-linking-recommendation/save-module-details`,
        payload
      );
      if (result) {
        trackAnalyticsEventCustom(eventTrackDetails);
        const path = `/app/discoverability/autonomous-internal-linking/[id]`;
        const as = `/app/discoverability/autonomous-internal-linking/${moduleId}`;
        Router.push(path, as);
        // await delayTimer(1000);
        // onCloseHandler();
      } else {
        const failedTrackingDetails = {
          ...eventTrackDetails,
          eventName: 'Failed To Add Linking Module',
        };
        trackAnalyticsEventCustom(failedTrackingDetails);
        console.error('ResultError :: AddNewLink :: error while creating new linking', result);
      }
      // setAddNewLinkLoading(false);
    } catch (error: any) {
      console.error('Error :: AddNewLink :: error while creating new linking', error);
      const obj = error?.response?.data?.data || error?.response?.data?.message;
      if ((typeof obj === 'object' && obj?.module_id) || (typeof obj === 'object' && obj?.module_id)) {
        setSnackBar({ type: 'error', text: 'Linking Module Name already exists.', open: true });
      } else {
        setSnackBar({
          type: 'error',
          text: 'Oops, Something went wrong !!',
          open: true,
        });
      }
      setAddNewLinkLoading(false);
      const failedTrackingDetails = {
        ...eventTrackDetails,
        eventName: 'Failed To Add Linking Module',
      };
      trackAnalyticsEventCustom(failedTrackingDetails);
    }
  }, [linkName, setSnackBar, trackAnalyticsEventCustom]);

  return (
    <div className="add-new-link-wrapper">
      {(loginCustomerInfo?.additional_user_info?.internalLinkingForm && !isNewInternalLinking) ||
      (isNewInternalLinking && currentIlListDetails?.status === API_STATUS.SUCCESS) ? (
        <Button
          className="primary-button font-500-ft12-lh16 text-white text-title-case radius-0"
          data-testid="add-new-link-button"
          onClick={() => {
            addNewPageBoxHandler();
          }}
          // endIcon={<AddIcon />}
        >
          Add Linking Module
        </Button>
      ) : null}
      <Dialog
        PaperProps={{
          style: {
            width: '80%',
            minWidth: 300,
            borderRadius: '4px',
            height: '90%',
          },
        }}
        onClose={addNewPageBoxHandler}
        aria-labelledby="simple-dialog-title"
        className={''}
        maxWidth="sm"
        open={openAddNewPage}
        data-testid="addNewKeywordDlgBoxWrapper"
      >
        <Close
          onClick={addNewPageBoxHandler}
          style={{ position: 'absolute', right: '20px', top: '5px', cursor: 'pointer' }}
        />

        <iframe
          sandbox="allow-forms allow-scripts allow-same-origin"
          src={loginCustomerInfo?.additional_user_info?.internalLinkingForm}
          title="airtable-form"
          width="100%"
          height="100%"
          onLoad={hideLoader}
          style={{ border: 'none' }}
        ></iframe>
        {loader ? <ThreeBarLoader /> : null}
      </Dialog>

      <Dialog open={isOpenAutoNomousILForm} maxWidth="sm" className="add-new-link-form-popup-wrapper">
        <DialogTitle>
          <p className="m-0 title">Enter Linking Module Name</p>
          <Close
            onClick={onCloseHandler}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              cursor: 'pointer',
            }}
          />
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            variant="outlined"
            margin="normal"
            value={linkName}
            onChange={(e) => setLinkName(e?.target?.value)}
          />
          {moduleNameError ? <Typography className="name-input-error">{moduleNameError}</Typography> : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={addNewLinkHandler}
            variant="contained"
            color="primary"
            style={{ textTransform: 'none' }}
            className="primary-button font-500-ft12-lh16 text-white text-title-case radius-0"
            disabled={addNewLinkLoading}
          >
            {addNewLinkLoading && <CircularProgress className="position-abs" size={20} />}
            Add New Link
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default React.memo(AddNewLink);
